import { useEffect, useRef } from 'react';
import type { LegalTemplate } from '@/mocks/superAdminLegalTemplates';
import { getActiveVersion, MOCK_PREVIEW_DATA } from '@/mocks/superAdminLegalTemplates';

interface TemplatePreviewModalProps {
  template: LegalTemplate | null;
  onClose: () => void;
}

export default function TemplatePreviewModal({ template, onClose }: TemplatePreviewModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  if (!template) return null;

  const activeVersion = getActiveVersion(template);

  const replaceVariables = (text: string): string => {
    return text.replace(
      /\{\{[a-z_]+\}\}/g,
      (match) => MOCK_PREVIEW_DATA[match] || match,
    );
  };

  const previewContent = replaceVariables(activeVersion.content);
  const previewTitle = replaceVariables(activeVersion.title);

  // Split into paragraphs
  const paragraphs = previewContent.split('\n\n').filter((p) => p.trim());

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[75] flex items-start justify-center p-4 pt-10 pb-10 bg-black/60 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="relative w-full max-w-[850px] bg-background-50 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-background-200 sticky top-0 bg-background-50 rounded-t-2xl z-10">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center shrink-0">
              <i className={`${template.typeIcon} text-foreground-500 text-lg`}></i>
            </div>
            <div className="min-w-0">
              <h3 className="text-foreground-950 font-bold text-sm">{template.typeLabel}</h3>
              <p className="text-foreground-400 text-[10px] flex items-center gap-1.5">
                <span>v{activeVersion.versionNumber} — {activeVersion.status === 'active' ? 'Versión activa' : activeVersion.status === 'draft' ? 'Borrador' : 'Archivada'}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-50 text-sky-600 text-[9px] font-medium border border-sky-200">
              <i className="ri-eye-line text-[9px]"></i>
              Vista previa con datos mock
            </span>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
            >
              <i className="ri-close-line text-foreground-500 text-sm"></i>
            </button>
          </div>
        </div>

        {/* Document content */}
        <div className="p-6 md:p-8 space-y-5 max-h-[65vh] overflow-y-auto">
          {/* Document header */}
          <div className="text-center space-y-3 pb-5 border-b-2 border-foreground-200">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-foreground-950 flex items-center justify-center">
              <i className={`${template.typeIcon} text-background-50 text-xl`}></i>
            </div>
            <h2 className="text-foreground-950 font-bold text-base uppercase tracking-wider">
              {previewTitle}
            </h2>
            <div className="flex items-center justify-center gap-2 text-foreground-400 text-[10px]">
              <span>RadioFrecuenciaWeb</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>v{activeVersion.versionNumber}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{activeVersion.createdBy}</span>
            </div>
          </div>

          {/* Content paragraphs */}
          <div className="space-y-4">
            {paragraphs.map((para, i) => {
              const isHeading = /^[A-ZÁÉÍÓÚÑ][^a-záéíóúñ]{2,}/.test(para.trim()) && para.trim().length < 120 && !para.includes('.');
              const isVariable = /\{\{[a-z_]+\}\}/.test(para);

              return (
                <div key={i} className={isHeading ? '' : ''}>
                  {isHeading ? (
                    <h3 className="text-foreground-900 text-sm font-bold uppercase tracking-wider mt-6 mb-3">
                      {para.replace(/\n/g, ' ').trim()}
                    </h3>
                  ) : (
                    <p className={`text-foreground-600 text-xs leading-relaxed ${para.trim().startsWith('-') || para.trim().startsWith('•') || para.trim().match(/^[a-z]\)/) ? 'pl-4' : ''}`}>
                      {para.split('\n').map((line, j) => {
                        const isListItem = line.trim().startsWith('-') || line.trim().startsWith('•') || line.trim().match(/^[a-z]\)/);
                        return (
                          <span key={j}>
                            {isListItem ? (
                              <span className="block text-foreground-600 text-xs leading-relaxed mb-1">{line.trim()}</span>
                            ) : (
                              <span>{line}{j < para.split('\n').length - 1 ? <br /> : null}</span>
                            )}
                          </span>
                        );
                      })}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Variables legend */}
          <div className="bg-sky-50/60 border border-sky-200 rounded-2xl p-4 mt-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-sky-100 flex items-center justify-center">
                <i className="ri-code-s-slash-line text-sky-500 text-sm"></i>
              </div>
              <p className="text-sky-700 text-[11px] font-bold uppercase tracking-wider">
                Variables dinámicas reemplazadas en esta vista previa
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {Object.entries(MOCK_PREVIEW_DATA).map(([key, value]) => (
                <div key={key} className="flex items-center gap-1.5 text-[10px]">
                  <span className="text-foreground-300 font-mono shrink-0">{key}</span>
                  <span className="text-foreground-400">→</span>
                  <span className="text-foreground-700 font-medium truncate">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-background-200">
          <p className="text-foreground-300 text-[10px]">
            Datos mock de ejemplo — Los valores reales dependen de cada radio y contrato
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cerrar Vista Previa
          </button>
        </div>
      </div>
    </div>
  );
}